import { useEffect, useRef } from 'react';
import * as THREE from 'three';

const COUNT = 7000;
const HOLD_MS = 5000;

function buildTargets(count) {
  const sphere = new Float32Array(count * 3);
  const knot = new Float32Array(count * 3);
  const wave = new Float32Array(count * 3);
  const rand = new Float32Array(count);

  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i++) {
    const i3 = i * 3;

    // Fibonacci sphere
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = golden * i;
    const R = 1.6;
    sphere[i3] = Math.cos(theta) * r * R;
    sphere[i3 + 1] = y * R;
    sphere[i3 + 2] = Math.sin(theta) * r * R;

    // Torus knot (p=2, q=3) with tube thickness
    const t = (i / count) * Math.PI * 2;
    const p = 2, q = 3, Rk = 1.05, rk = 0.42;
    const tube = 0.22 * Math.sqrt(Math.random());
    const ang = Math.random() * Math.PI * 2;
    const cr = Rk + rk * Math.cos(q * t);
    knot[i3] = cr * Math.cos(p * t) + Math.cos(ang) * tube;
    knot[i3 + 1] = cr * Math.sin(p * t) + Math.sin(ang) * tube;
    knot[i3 + 2] = rk * Math.sin(q * t) + (Math.random() - 0.5) * tube;

    // Grid plane
    const side = Math.ceil(Math.sqrt(count));
    const gx = (i % side) / side - 0.5;
    const gy = Math.floor(i / side) / side - 0.5;
    wave[i3] = gx * 4.4;
    wave[i3 + 1] = gy * 2.8;
    wave[i3 + 2] = 0;

    rand[i] = Math.random();
  }
  return { sphere, knot, wave, rand };
}

const VERT = /* glsl */ `
  uniform float uTime;
  uniform float uMorph;
  uniform float uScroll;
  uniform float uPixelRatio;
  uniform vec2  uMouse;
  uniform float uTanHalfFov;
  uniform float uAspect;

  attribute vec3  aSphere;
  attribute vec3  aKnot;
  attribute vec3  aWave;
  attribute float aRand;

  varying float vGlow;
  varying float vAlpha;

  void main() {
    float seg = mod(uMorph, 3.0);
    vec3 p;
    if (seg < 1.0)      p = mix(aSphere, aKnot, smoothstep(0.0, 1.0, seg));
    else if (seg < 2.0) p = mix(aKnot, aWave, smoothstep(0.0, 1.0, seg - 1.0));
    else                p = mix(aWave, aSphere, smoothstep(0.0, 1.0, seg - 2.0));

    // gentle breathing
    p.z += sin(p.x * 2.2 + uTime * 1.4) * sin(p.y * 2.0 + uTime * 0.9) * 0.18;
    p   += normalize(p + 0.001) * sin(uTime * 0.8 + aRand * 6.28) * 0.03;

    // scroll: disperse outward
    p += normalize(p + 0.001) * uScroll * (2.0 + aRand * 3.0);

    vec4 mv = modelViewMatrix * vec4(p, 1.0);

    // mouse repulsion in view space
    float depth = -mv.z;
    vec2 halfSize = vec2(uTanHalfFov * depth * uAspect, uTanHalfFov * depth);
    vec2 mouseView = uMouse * halfSize;
    vec2 d = mv.xy - mouseView;
    float dist = length(d);
    float force = smoothstep(1.1, 0.0, dist);
    mv.xy += normalize(d + 0.0001) * force * 0.55;

    vGlow  = force;
    vAlpha = (0.55 + aRand * 0.45) * (1.0 - uScroll * 0.95);

    gl_Position  = projectionMatrix * mv;
    gl_PointSize = (1.6 + aRand * 1.8 + force * 3.5) * uPixelRatio * (5.0 / depth);
  }
`;

const FRAG = /* glsl */ `
  varying float vGlow;
  varying float vAlpha;

  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float d = length(c);
    if (d > 0.5) discard;
    float soft = smoothstep(0.5, 0.15, d);

    vec3 base  = vec3(0.0, 0.569, 1.0);   // #0091ff
    vec3 cool  = vec3(0.0, 0.76, 1.0);    // #00c2ff
    vec3 hot   = vec3(0.635, 0.349, 1.0); // #a259ff
    vec3 col = mix(base, cool, vGlow * 0.6);
    col = mix(col, hot, vGlow * vGlow);

    gl_FragColor = vec4(col, soft * vAlpha);
  }
`;

function HeroScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: 'high-performance' });
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    renderer.setPixelRatio(pixelRatio);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 50);
    camera.position.z = 5.2;

    const { sphere, knot, wave, rand } = buildTargets(COUNT);
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(sphere, 3));
    geo.setAttribute('aSphere', new THREE.BufferAttribute(sphere, 3));
    geo.setAttribute('aKnot', new THREE.BufferAttribute(knot, 3));
    geo.setAttribute('aWave', new THREE.BufferAttribute(wave, 3));
    geo.setAttribute('aRand', new THREE.BufferAttribute(rand, 1));

    const uniforms = {
      uTime: { value: 0 },
      uMorph: { value: 0 },
      uScroll: { value: 0 },
      uPixelRatio: { value: pixelRatio },
      uMouse: { value: new THREE.Vector2(10, 10) },
      uTanHalfFov: { value: Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) },
      uAspect: { value: 1 },
    };

    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geo, mat);
    const group = new THREE.Group();
    group.add(points);
    scene.add(group);

    const mouse = new THREE.Vector2(10, 10);
    const targetMouse = new THREE.Vector2(10, 10);
    const targetRot = new THREE.Vector2(0, 0);
    let morphTarget = 0;
    let scrollT = 0;
    let lastMorph = performance.now();

    function resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      uniforms.uAspect.value = camera.aspect;
      group.scale.setScalar(w < 700 ? 0.7 : 1);
    }

    function onMove(e) {
      targetMouse.set((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1);
      targetRot.set(targetMouse.y * 0.25, targetMouse.x * 0.35);
    }
    function onLeave() {
      targetMouse.set(10, 10);
    }
    function onScroll() {
      scrollT = THREE.MathUtils.clamp(window.scrollY / (window.innerHeight * 0.85), 0, 1);
    }
    function onClick(e) {
      if (scrollT > 0.4) return;
      if (e.target.closest('a, button, input, textarea')) return;
      morphTarget += 1;
      lastMorph = performance.now();
    }

    resize();
    onScroll();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseout', onLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('click', onClick);

    const clock = new THREE.Clock();
    let raf;
    let running = true;

    function tick() {
      if (!running) return;
      raf = requestAnimationFrame(tick);

      const now = performance.now();
      if (now - lastMorph > HOLD_MS) {
        morphTarget += 1;
        lastMorph = now;
      }

      const t = clock.getElapsedTime();
      uniforms.uTime.value = t;
      uniforms.uMorph.value += (morphTarget - uniforms.uMorph.value) * 0.035;
      uniforms.uScroll.value += (scrollT - uniforms.uScroll.value) * 0.08;

      mouse.lerp(targetMouse, 0.1);
      uniforms.uMouse.value.copy(mouse);

      group.rotation.y += ((targetRot.y + t * 0.12) - group.rotation.y) * 0.05;
      group.rotation.x += (targetRot.x - group.rotation.x) * 0.05;

      if (uniforms.uScroll.value > 0.98) {
        renderer.clear();
        return;
      }
      renderer.render(scene, camera);
    }
    tick();

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseout', onLeave);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('click', onClick);
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="hero-3d" aria-hidden="true" />;
}

export default HeroScene;
